import dayjs from 'dayjs';
import Vue from 'vue';
import { store } from './store.js';
import BatchPromise from './batchPromise.js';

import { VueResourceUrls, VueResourceData, VueResourceHeaders } from './vue-resource.js';

class AppUsageTracker {
	constructor(descriptor, usageListItems) {
		this.descriptor = descriptor;
		this.localStorageId = null;
		this.usageListItems = usageListItems;
		this.usageTrackerInfo = null;
	}

	getBatchInfo(entries, user) {
		const aux = entries.reduce((obj, entry) => {
			const { entryMerged, id } = this.mergeInfo(entry);

			obj.urls = obj.urls || [];
			obj.urls.push(VueResourceUrls.saveUsageTracker(store.state.stateMachine.testMode, id));

			obj.data = obj.data || [];
			obj.data.push(VueResourceData.saveUsageTracker(store.state.stateMachine.testMode, entryMerged[0], entryMerged[1], user, this.descriptor));

			return obj;
		}, {});
		return aux;
	}

	flush() {
		return new Promise((resolve, reject) => {
			this.loadOldUsageData().then((responseLoad) => {
				if (responseLoad) {
					this.filterOutOldUsageData(responseLoad).then(({ dataToSave, dataToKeep, user }) => {
						if (dataToSave && Object.keys(dataToSave).length !== 0) {
							let entries = Object.entries(dataToSave),
								useBatch = false;
							if (useBatch) {
								// You may have to make some modifications if you want to use the batch function with the latest changes.
								entries = this.getBatchInfo(entries, user);

								BatchPromise.call({
									urls: {
										post: [entries.urls],
									},
									data: {
										post: [entries.data],
									},
								}).then(() => {
									resolve({ ...dataToKeep, user });
									this.usageTrackerInfo = { ...dataToKeep, user };
								}).catch((error) => reject(error));
							} else {
								Promise.all(entries.map((e) => this.postData(e, user))).then(() => {
									resolve({ ...dataToKeep, user });
									this.usageTrackerInfo = { ...dataToKeep, user };
								}).catch((error) => reject(error));
							}
						}
					}).catch((error) => reject(error));
				} else {
					resolve();
				}
			}).catch((error) => console.error(error));
		});
	}

	initialize({ localStorageId, usageListItems, usageTrackerInfo }) {
		this.localStorageId = localStorageId;
		this.usageListItems = usageListItems;
		this.usageTrackerInfo = usageTrackerInfo;
	}

	loadOldUsageData() {
		return new Promise((resolve, reject) => {
			try {
				let info = this.usageTrackerInfo;
				if (!info) {
					info = (JSON.parse(localStorage.getItem(this.localStorageId), {})).usageTracker;
				}
				resolve(info);
			} catch (e) {
				console.error(`[${this.descriptor}] Error loading appUsage from localStorage`);
				reject(e);
			}
		});
	}

	filterOutOldUsageData(data) {
		return new Promise((resolve, reject) => {
			try {
				let { user, ...object } = data,
					keys = Object.keys(object),
					currentDate = dayjs().utc().format('YYYYMMDD'),
					dataToSave = keys.reduce((obj, k) => {
						if (dayjs(k).isBefore(currentDate)) {
							obj[k] = object[k];
						}
						return obj;
					}, {}),
					dataToKeep = keys.reduce((obj, k) => {
						if (dayjs(k).isSameOrAfter(currentDate)) {
							obj[k] = object[k];
						}
						return obj;
					}, {});

				resolve({ dataToSave, dataToKeep, user });
			} catch (e) {
				console.error(`[${this.descriptor}] Error parsing appUsage on filterOutOldUsageData`);
				reject(e);
			}
		});
	}

	mergeInfo(entry) {
		const usageTracker = this.usageListItems ? this.usageListItems.find((us) => us.dateYMD === entry[0] && us.appCode === this.descriptor) : null;
		let id = null;

		if (usageTracker) {
			entry[1].push(...usageTracker.data);
			id = usageTracker.id;
		}

		return { entryMerged: entry, id };
	}

	postData(entry, user) {
		const { entryMerged, id } = this.mergeInfo(entry),
			restCallUrl = VueResourceUrls.saveUsageTracker(store.state.stateMachine.testMode, id),
			restCallHeaders = VueResourceHeaders.standardSaveHeaders(id),
			restCallData = VueResourceData.saveUsageTracker(store.state.stateMachine.testMode, entryMerged[0], entryMerged[1], user, this.descriptor);
		return new Promise((resolve, reject) => {
			Vue.http.post(restCallUrl, restCallData, restCallHeaders).then((responses) => {
				resolve(responses);
			}).catch((error) => {
				console.error('[PRODUCTS] Error at getSeriesSiteLists', error);
				reject(error);
			});
		});
	}
}

export { AppUsageTracker };
